import { Copy, Check, Code2, ChevronRight, EyeOff, Loader2 } from "lucide-react";
import { cn } from "@/lib/studio/utils";

const copyBtnClass =
  "flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors rounded hover:bg-muted/80";

const expandBtnBase =
  "flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-colors";

export function CodeBlockCopyButton({
  show,
  isCopied,
  onCopy,
}: {
  show: boolean;
  isCopied: boolean;
  onCopy: () => void;
}) {
  if (!show) return null;
  return (
    <button type="button" onClick={onCopy} className={copyBtnClass} title="Copy code">
      {isCopied ? (
        <>
          <Check className="h-3.5 w-3.5" />
          <span>Copied</span>
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          <span>Copy</span>
        </>
      )}
    </button>
  );
}

export function CodeBlockExpandToggle({
  isExpanded,
  onToggle,
}: {
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`${expandBtnBase} ${
        isExpanded
          ? "text-muted-foreground hover:text-foreground hover:bg-muted/80"
          : "text-primary bg-primary/10 hover:bg-primary/20"
      }`}
      title={isExpanded ? "Hide code" : "Show code"}
    >
      {isExpanded ? (
        <>
          <EyeOff className="h-3.5 w-3.5" />
          <span>Hide Code</span>
        </>
      ) : (
        <>
          <Code2 className="h-3.5 w-3.5" />
          <span>Show Code</span>
        </>
      )}
    </button>
  );
}

export function CodeBlockCollapsedPreview({
  codeString,
  lineCount,
  onToggleExpand,
  containerClassName,
  moreHintClassName,
  showMoreHint,
  previewLabel = "Code preview",
  detailHint,
  ariaLabel,
}: {
  codeString: string;
  lineCount: number;
  onToggleExpand: () => void;
  containerClassName?: string;
  moreHintClassName?: string;
  showMoreHint: boolean;
  previewLabel?: string;
  detailHint?: string;
  ariaLabel?: string;
}) {
  const defaultAria =
    ariaLabel ??
    (detailHint
      ? `Show full code: ${detailHint}, ${lineCount} lines`
      : `Show full code editor, ${lineCount} lines`);

  return (
    <button
      type="button"
      aria-expanded={false}
      aria-label={defaultAria}
      onClick={onToggleExpand}
      className={cn(
        "group relative w-full text-left rounded-b-md",
        "border-l-4 border-l-primary",
        "bg-muted/40 dark:bg-muted/35",
        "px-3 py-2.5",
        "ring-1 ring-inset ring-border/45",
        "cursor-pointer",
        "transition-[background-color,box-shadow,color] duration-150",
        "hover:bg-muted/65 dark:hover:bg-muted/50",
        "hover:ring-primary/45",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        containerClassName,
      )}
    >
      <div className="mb-1 flex items-start justify-between gap-2 border-b border-border/50 pb-2">
        <div className="min-w-0 flex flex-col gap-0.5">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-tight text-foreground">
            <Code2 className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
            <span>{previewLabel}</span>
          </span>
          {detailHint ? (
            <span className="truncate text-[10px] font-medium text-muted-foreground">
              {detailHint}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <span
            className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-primary"
            aria-hidden
          >
            {lineCount} lines
          </span>
          <ChevronRight
            className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary"
            aria-hidden
          />
        </div>
      </div>

      <div
        className="font-mono text-[11px] leading-5 text-foreground/90 dark:text-foreground/88"
        aria-hidden
      >
        {codeString.split("\n").slice(0, 3).map((line, i) => (
          <div key={i} className="truncate leading-5">
            {line || "\u00A0"}
          </div>
        ))}
      </div>
      {showMoreHint ? (
        <div
          className={cn(
            "mt-2 text-center text-[10px] font-medium text-primary/90 dark:text-primary/80",
            moreHintClassName,
          )}
        >
          +{lineCount - 3} more lines — click to expand
        </div>
      ) : null}
    </button>
  );
}

export function StreamingIndicator({ active }: { active: boolean }) {
  if (!active) return null;
  return <Loader2 className="h-3 w-3 animate-spin text-primary" />;
}
